import { Platform } from "react-native";
import NetInfo from "@react-native-community/netinfo";
import { onlineManager, QueryClient } from "@tanstack/react-query";

import { installMutationDefaults } from "~/state/mutations";

/**
 * florafolio is offline-first: the whole authenticated query cache is persisted
 * to AsyncStorage (see `query-persist.ts`) and writes are queued while offline
 * and replayed on reconnect (see `state/mutations.ts`).
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Persisted rows render instantly and every mutation invalidates its
      // own queries, so nothing here needs a fresh network read on a mere
      // screen refocus. A long staleTime keeps the offline mirror showing
      // and only refetches when data is genuinely stale — far less API
      // traffic for a single-user collection that changes a few times a day.
      staleTime: 15 * 60_000,
      gcTime: Infinity,
      retry: 1,
      refetchOnWindowFocus: false,
      networkMode: "offlineFirst",
    },
    mutations: {
      // Run the optimistic update immediately; if the request can't reach the
      // server, pause the mutation instead of failing it. Paused mutations are
      // persisted and resumed by `resumePausedMutations()` after a reconnect or
      // an app restart.
      networkMode: "offlineFirst",
      retry: 2,
    },
  },
});

/**
 * On native, drive react-query's online/offline signal from the OS network
 * state via NetInfo. On web, react-query's built-in listener (window
 * `online`/`offline` events) is already correct — overriding it with NetInfo's
 * web shim actually makes offline detection worse, so leave it alone.
 */
if (Platform.OS !== "web") {
  onlineManager.setEventListener((setOnline) =>
    NetInfo.addEventListener((state) => {
      setOnline(state.isConnected !== false);
    }),
  );
}

// Register the write functions before the persister restores, so a mutation
// that was queued offline in a previous session can be looked up by key and
// replayed by `resumePausedMutations()`.
installMutationDefaults(queryClient);
